import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { breakpoints, colors, mixins, respondFrom, css, respondTo } from '@styles';
import Collapsible from 'react-collapsible';

const StyledAccordion = styled.div`
    .Collapsible {
        margin: 20px 0;
        ${respondTo(
            breakpoints.lg,
                css`
                    &.is-open {
                    .Collapsible__contentOuter {
                        height: 100% !important;
                    }
                }
                &__contentOuter {
                    position: fixed;
                    left: 0px;
                    bottom: 0px;
                    right: 0px;
                    background-color: ${colors.text.default};
                    z-index: 100;
                };
                &__contentInner {
                    height: calc(100vh - 54px); /* Fallback for browsers that do not support Custom Properties */
                    height: calc((var(--vh, 1vh) * 100) - 54px);
                    margin-top: 54px;
                    padding: 20px 15px 55px 15px;
                    overflow-y: auto;
                    z-index: 101;
                    position: relative;
                };
            `
        )};
        
        &__trigger {
            display: block;
            position: relative;
            font-size: 14px;
            font-weight: bold;
            color: ${colors.white};
            cursor: pointer;
            padding-right: 25px;

            &::before {
                content: '';
                position: absolute;
                top: -5px;
                bottom: 0;
                right: 0;
                margin: auto;
                width: 12px;
                height: 12px;
                border: 3px solid ${colors.white};
                border-left: 0;
                border-top: 0;
                transform: rotate(45deg);
                transform-origin: center;
                ${mixins.transitionDefault};
                z-index: 99;
            }

            &.is-open {
                &::before {
                    transform: translate(0, 5px) rotate(225deg);
                }
            }

            &.is-disabled {
                pointer-events: none;
                opacity: 0.5;
            }
        }
    }
`;

interface Accordion {
    trigger: string | React.ReactElement;
    onOpen?: Function;
    children: any;
}

const StyledAccordionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 14px;
    font-weight: bold;
    color: ${colors.white};
    height: 100%;
`;

const StyledAccordionHeader = styled.div`
    ${respondFrom(
        breakpoints.lg,
        css`
            display: none;
        `
    )};
`;

const StyledAccordionChildren = styled.div``;

const StyledAccordionTriggerWhenOpen = styled.div`
    position: fixed;
    bottom: 30px;
    left: 0;
    right: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    margin-top: auto;
    padding: 20px 0;
    z-index: 101;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s, visibility 0.3s;
    &.is-open {
        opacity: 1;
        visibility: visible;
        transition-delay: 0.3s;
    }
    ${respondFrom(
        breakpoints.lg,
        css`
            display: none;
        `
    )};
    &:after {
        content: '';
        margin-left: 20px;
        width: 12px;
        height: 12px;
        border: 3px solid ${colors.white};
        border-left: 0;
        border-top: 0;
        transform: translate(0, 2px) rotate(225deg);
        transform-origin: center;
        ${mixins.transitionDefault};
    }
`;
interface Accordion {
    trigger: string | React.ReactElement<any>;
    triggerWhenOpen: string;
    triggerDisabled?: boolean;
    onOpen?: Function;
    children: any;
}

const Accordion = ({ trigger, triggerWhenOpen, triggerDisabled, onOpen, children }: Accordion) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if(triggerDisabled) setOpen(false);
    }, [triggerDisabled]);

    return (
        <StyledAccordion>
            <Collapsible 
                open={open} 
                openedClassName="is-open" 
                handleTriggerClick={() => setOpen(!open)} 
                trigger={trigger} 
                triggerDisabled={triggerDisabled}
                triggerTagName="div"
                onOpen={() => onOpen && onOpen()}
            >
                <StyledAccordionWrapper>
                    <StyledAccordionHeader>
                        {trigger}
                    </StyledAccordionHeader>
                    <StyledAccordionChildren>
                        {children}
                    </StyledAccordionChildren>
                    <StyledAccordionTriggerWhenOpen className={open ? 'is-open' : ''} onClick={() => setOpen(false)}>
                        {triggerWhenOpen}
                    </StyledAccordionTriggerWhenOpen>
                </StyledAccordionWrapper>
            </Collapsible>
        </StyledAccordion>
    )
}

export default Accordion;
